// React and third-party libraries
import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Layout and UI components
import GeaoVerticalStepperLayout from "../../shared/Layout/GeaoVerticalStepperLayout";
import EmployeeInfoSection from "../../shared/PersonalInformation/EmployeeInfoSection";
import LinesOfBusinessSection from "../../shared/PersonalInformation/LinesOfBusinessSection";
import LocationInfoSection from "../../shared/PersonalInformation/LocationInfoSection";
import PersonalInfoSection from "../../shared/PersonalInformation/PersonalInfoSection";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import SelectBox from "../../../../gds/SelectBox";

// Context and constants
import { GeaoContext } from "../../../GeaoContextProvider";
import { ClientConfigurationContext } from "../../../../../context/ClientConfigurationContextProvider";
import { getProducerFlowVerticalStepperElements } from "../../../constants/ProducerFlowVerticalStepperElementsFactory";
import WorkArrangementOptions from "../../../constants/options/WorkArrangementOptions";

// Hooks
import useTitle from "../../../../../hooks/useTitle";
import useFormEvents from "./useFormEvents";
import useOfficeReportsToOptions from "./useOfficeReportsToOptions";

// Utils and validation
import { interviewToFormData } from "./mappers";
import validationSchema from "./validation";
import useGtmTracking from "../../../../../hooks/useGtmTracking";

/**
 * ProducerPersonalInformation component renders a comprehensive form for collecting
 * producer personal information during the GEAO onboarding process.
 * 
 * @returns {JSX.Element} The producer personal information form component
 */
export default function ProducerPersonalInformation() {
    // Set the page title for accessibility and navigation
    useTitle("Personal Information");

    useGtmTracking("Onboarding:AgentPersonalInformation", "onboarding_agent_in_progress");

    /** @type {Object} Context containing agent interview data and completion status */
    const { agentInterview, interviewIsCompleted } = useContext(GeaoContext);
    const clientConfiguration = useContext(ClientConfigurationContext);

    /** @type {Object} Available parent branch options for office selection and loading state */
    const { branchOptions, areOfficesLoading } = useOfficeReportsToOptions();

    const enableOffshore = clientConfiguration?.enableOffshore === true;
    const isOffshoreResident = agentInterview?.questionAnswers?.OffshoreResident?.answer === "true";
    const verticalStepperElements = getProducerFlowVerticalStepperElements(enableOffshore, isOffshoreResident);

    const formDefaults = interviewToFormData(agentInterview);
    // Only allow offshore behavior when feature flag is enabled
    if (!enableOffshore) {
        formDefaults.isOffshoreResident = false;
    }

    /**
     * Initialize react-hook-form with validation schema and default values
     * Maps existing interview data to form structure
     */
    const useFormReturn = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: formDefaults
    });

    /** Form control methods for handling submission, validation, and field management */
    const { handleSubmit, getValues, trigger, control } = useFormReturn;

    /**
     * Custom hook that provides form event handlers and saving state
     * Handles form submission, save and exit functionality
     */
    const {
        onSubmit,
        onSaveAndExit,
        saving
    } = useFormEvents(getValues, trigger, branchOptions);

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={verticalStepperElements}
            currentStepperItem="Personal Information"
            header="Personal Information"
        >
            <FormProvider {...useFormReturn}>
                <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                    <PersonalInfoSection />
                    <LocationInfoSection />
                    <EmployeeInfoSection showLicensedProducerQuestions={true} />
                    <LinesOfBusinessSection />

                    <h2 className="mb-5">Agency Office Info</h2>

                    {branchOptions?.length > 0 &&
                        <SelectBox
                            control={control}
                            name="parentBranchSob"
                            label="Please select the office you report to from the list below:"
                            placeholder="Please Select"
                            options={branchOptions}
                        />
                    }

                    <RadioButtonGroup
                        control={control}
                        name="workArrangement"
                        legend="What is your current work arrangement?"
                        descriptiveCopy="Select how you primarily work."
                        layout="One Column"
                        options={WorkArrangementOptions}
                    />

                    <div className="row">
                        <div className="col-sm-12">
                            <button
                                type="submit"
                                className="btn btn--primary btn--full-mobile btn--pull-right"
                                disabled={saving || interviewIsCompleted || areOfficesLoading}
                            >
                                <span>Next</span>
                            </button>

                            <button
                                type="button"
                                className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                                disabled={saving || interviewIsCompleted || areOfficesLoading}
                                onClick={() => {
                                    handleSubmit(onSaveAndExit)();
                                }}
                            >
                                <span>Save And Exit</span>
                            </button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </GeaoVerticalStepperLayout>
    );
}
